import React, { useRef } from "react";
import { useTranslation } from "react-i18next";
import * as Dialog from "@radix-ui/react-dialog";
import { X } from "lucide-react";

import { TITLE_BAR_HEIGHT, hasCustomTitleBar } from "../../utils/desktopShell";
import ModalDragStrip from "../ModalDragStrip";
import ScreenshotCarousel, {
  type ScreenshotRef,
  type ScreenshotUrlResolver,
} from "./ScreenshotCarousel";

type ScreenshotCarouselDialogProps = {
  open: boolean;
  screenshots: ScreenshotRef[];
  onOpenChange: (open: boolean) => void;
  initialIndex?: number;
  onIndexChange?: (index: number) => void;
  counterLabel?: React.ReactNode;
  navigationDetails?: React.ReactNode;
  resolveUrl?: ScreenshotUrlResolver;
  title?: React.ReactNode;
  footer?: React.ReactNode;
  onCloseAutoFocus?: (event: Event) => void;
  isDimmed?: (screenshot: ScreenshotRef) => boolean;
  slideAction?: (screenshot: ScreenshotRef) => React.ReactNode;
};

const ScreenshotCarouselDialog = ({
  open,
  screenshots,
  onOpenChange,
  initialIndex,
  onIndexChange,
  counterLabel,
  navigationDetails,
  resolveUrl,
  title,
  footer,
  onCloseAutoFocus,
  isDimmed,
  slideAction,
}: ScreenshotCarouselDialogProps) => {
  const { t } = useTranslation();
  const returnFocusRef = useRef<HTMLElement | null>(null);
  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-50 bg-black/60" />
        <Dialog.Content
          className="fixed inset-0 z-50 p-2 sm:p-3"
          // Below the window's drag strip: Electron resolves drag regions geometrically, so a
          // close button inside the strip would drag the window instead of closing the dialog.
          style={hasCustomTitleBar ? { paddingTop: TITLE_BAR_HEIGHT } : undefined}
          aria-describedby={undefined}
          onOpenAutoFocus={event => {
            returnFocusRef.current =
              document.activeElement instanceof HTMLElement ? document.activeElement : null;
            event.preventDefault();
          }}
          onCloseAutoFocus={event => {
            onCloseAutoFocus?.(event);
            if (event.defaultPrevented) return;
            event.preventDefault();
            if (returnFocusRef.current?.isConnected)
              returnFocusRef.current.focus({ preventScroll: true });
          }}
        >
          <Dialog.Title className="sr-only">{title ?? t("screenshots")}</Dialog.Title>
          <ModalDragStrip />
          <div className="relative z-10 flex h-full w-full min-h-0 flex-col">
            <Dialog.Close
              aria-label={t("close")}
              className="absolute right-3 top-3 z-10 rounded-full border border-outline bg-surface/90 p-2 text-text-dim shadow hover:text-text transition"
            >
              <X className="h-4 w-4" />
            </Dialog.Close>
            <div className="min-h-0 flex-1">
              <ScreenshotCarousel
                fullscreen
                screenshots={screenshots}
                initialIndex={initialIndex}
                onIndexChange={onIndexChange}
                counterLabel={counterLabel}
                navigationDetails={navigationDetails}
                resolveUrl={resolveUrl}
                isDimmed={isDimmed}
                slideAction={slideAction}
              />
            </div>
            {footer && (
              <div className="shrink-0 border-t border-outline bg-surface p-3">{footer}</div>
            )}
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
};

export default ScreenshotCarouselDialog;
